import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { getSessions, closeSession, closeOtherSessions, errorMessage } from '../services/api';
import { agentLabel, describeAgent, isLocalIp, upperFirst } from '../utils/kernelDevice';
import { fmtDateTime } from '../utils/kernelFormat';
import './kernelSessions.css';

const DeviceIcon = ({ mobile }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {mobile ? (
      <>
        <rect x="7" y="2" width="10" height="20" rx="2" />
        <line x1="11" y1="18" x2="13" y2="18" />
      </>
    ) : (
      <>
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <line x1="8" y1="20" x2="16" y2="20" />
        <line x1="12" y1="16" x2="12" y2="20" />
      </>
    )}
  </svg>
);

/**
 * Una sessione di accesso (dispositivo) in elenco: browser e sistema, IP, ultima attività e, se si passa `onClose`,
 * il pulsante per chiuderla. Usata dal Profilo (KernelSessions) e dalla scelta al limite di accessi (KernelSessionLimit).
 */
export function KernelSessionItem({ session, onClose, closeLabel, busy }) {
  const { t } = useLanguage();
  const label = agentLabel(session.user_agent) || t('sessions.unknownDevice');
  const mobile = describeAgent(session.user_agent).mobile;
  return (
    <li className={'kernel-session' + (session.current ? ' is-current' : '')}>
      <span className="kernel-session-icon"><DeviceIcon mobile={mobile} /></span>
      <span className="kernel-session-text">
        <span className="kernel-session-title">
          {label}
          {session.current && <span className="kernel-session-badge">{t('sessions.current')}</span>}
        </span>
        <span className="kernel-session-meta">
          {[isLocalIp(session.ip) ? upperFirst(t('sessions.localIp')) : session.ip,`${t('sessions.lastSeen')} ${fmtDateTime(session.last_seen_at)}`].filter(Boolean).join(' · ')}
        </span>
      </span>
      {onClose && (
        <button type="button" className="btn btn-secondary btn-sm kernel-session-close" disabled={busy} onClick={() => onClose(session)}>
          {closeLabel}
        </button>
      )}
    </li>
  );
}

/**
 * Sezione «Dispositivi collegati» del Profilo: elenco delle sessioni attive dell'utente, con la possibilità di chiuderne una
 * o tutte le altre. Chiudere quella corrente equivale a uscire.
 */
function KernelSessions({ onLogout }) {
  const { t } = useLanguage();
  const [sessions, setSessions] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getSessions()
      .then((res) => setSessions(res.sessions || []))
      .catch((err) => setError(errorMessage(err)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const close = async (session) => {
    setBusy(true);
    setError('');
    try {
      await closeSession(session.sid);
      if (session.current && onLogout) {
        onLogout();
        return;
      }
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const closeOthers = async () => {
    setBusy(true);
    setError('');
    try {
      await closeOtherSessions();
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (sessions === null && !error) return null;
  const others = (sessions || []).filter((s) => !s.current).length;

  return (
    <section className="card kernel-sessions" aria-labelledby="kernel-sessions-title">
      <div className="card-header">
        <h3 id="kernel-sessions-title">{t('sessions.title')}</h3>
        {others > 0 && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={closeOthers}>
            {t('sessions.closeOthers')}
          </button>
        )}
      </div>
      <div className="card-body">
        <p className="page-subtitle" style={{ marginTop: 0 }}>{t('sessions.hint')}</p>
        {error && <div className="error-message">{error}</div>}
        {sessions && sessions.length === 0 && <p className="kernel-session-empty">{t('sessions.none')}</p>}
        {sessions && sessions.length > 0 && (
          <ul className="kernel-session-list">
            {sessions.map((s) => (
              <KernelSessionItem
                key={s.sid}
                session={s}
                busy={busy}
                onClose={close}
                closeLabel={s.current ? t('sessions.logout') : t('sessions.close')}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default KernelSessions;
